import React, { useState } from "react";
import { css } from "@emotion/react";
import {
  Label,
  Font,
  TextLink,
  Icon,
  Button,
} from "@moneyforward/mfx_biz_react_components";
import { useIsSpMode } from "@/hooks";
import { postalCodeValidation } from "@/components/validations";
import { useFormContext, UseFormReturn } from "react-hook-form";
import { FormErrorMessage, FormTextField } from "@/components/molecules";
import useFeatureFlags from "@/libs/hooks/useFeatureFlags";
import { color } from "@/styles/theme";

type Props = {
  nameForward: string;
  nameEnd: string;
  labelRequired: boolean;
  onClickSearchAddress: (postalCode: string) => void;
};

const PostalCodeFormFieldMain = (
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const isSpMode = useIsSpMode();
  const { getValues } = useFormContext();

  const { featureFlags, loading, error } = useFeatureFlags();

  if (loading) {
    return <p>Loading...</p>;
  }
  if (error) {
    return <p>Error: {error.message}</p>;
  }

  const enableKenallAddressAssist = featureFlags.KenAllAddressAssist.Enabled;

  return (
    <div css={!isSpMode ? container : {}}>
      <div css={labelWrapper}>
        <Font variant="m" component="p">
          郵便番号 {props.labelRequired && <Label variant="required" />}
        </Font>
      </div>
      <div css={inputWrapper}>
        <div css={textFieldWrapper}>
          <div css={postalCodeForwardWrapper}>
            <FormTextField
              name={props.nameForward}
              registerOptions={{
                validate: postalCodeValidation(3),
              }}
              maxLength={3}
              ref={ref}
            />
          </div>
          <p>-</p>
          <div css={postalCodeEndWrapper}>
            <FormTextField
              name={props.nameEnd}
              registerOptions={{
                validate: postalCodeValidation(4),
              }}
              maxLength={4}
              ref={ref}
            />
          </div>
          {enableKenallAddressAssist && (
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  const searchPostalCode = `${getValues().postalCodeForward}${
                    getValues().postalCodeEnd
                  }`;
                  props.onClickSearchAddress(searchPostalCode);
                }}
              >
                住所検索
              </Button>
            </div>
          )}
        </div>
        <div css={errorWrapper}>
          <FormErrorMessage name={props.nameForward} />
          <FormErrorMessage name={props.nameEnd} />
        </div>
        {enableKenallAddressAssist && (
          <>
            <div css={{ marginTop: "4px" }}>
              <Font variant="s" css={{ color: color.gray600 }}>
                郵便番号を入力し「住所検索」を押すと住所の検索結果が表示されます
              </Font>
            </div>
            <div css={{ marginTop: "4px" }}>
              <TextLink
                target="_blank"
                href="https://www.post.japanpost.jp/zipcode/index.html"
              >
                <span css={{ display: "flex", alignItems: "center" }}>
                  <span css={{ marginRight: 8 }}>郵便番号検索サイト</span>
                  <Icon
                    name="openInNew"
                    size="sm"
                    css={{ cursor: "pointer" }}
                  />
                </span>
              </TextLink>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const PostalCodeFormField = React.forwardRef(PostalCodeFormFieldMain);

const container = css({
  display: "flex",
});

const labelWrapper = css({
  width: "160px",
  marginRight: "16px",
  paddingTop: "10px",
  paddingBottom: "4px",
});

const inputWrapper = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  flex: "1 0 0",
});

const textFieldWrapper = css({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  alignSelf: "stretch",
});

const postalCodeForwardWrapper = css({
  width: "62px",
});

const postalCodeEndWrapper = css({
  width: "71px",
});

const errorWrapper = css({
  flex: 1,
});
